import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, Clock, MapPin, Hash, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { routeCityApi, cityApi } from '../services/api';

const RouteCityModal = ({ isOpen, onClose, routeId, routeName }) => {
  const [cities, setCities] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({
    cityID: '',
    arrivalTime: '',
    orderID: 1
  });

  // Load cities for the route
  const loadCities = async () => {
    if (!routeId) return;
    
    try {
      setLoading(true);
      const response = await routeCityApi.getRouteCitiesByRoute(routeId);
      setCities(response || []);
    } catch (error) {
      console.error('Error loading cities:', error);
      Swal.fire('Error', 'Failed to load cities', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load available cities from CityMaster
  const loadAvailableCities = async () => {
    try {
      setLoadingCities(true);
      const response = await cityApi.getCities(true); // Get only active cities
      setAvailableCities(response || []);
    } catch (error) {
      console.error('Error loading available cities:', error);
      Swal.fire('Error', 'Failed to load cities', 'error');
    } finally {
      setLoadingCities(false);
    }
  };

  // Load cities when modal opens
  useEffect(() => {
    if (isOpen && routeId) {
      loadCities();
      loadAvailableCities();
    }
  }, [isOpen, routeId]);

  // Reset form
  const resetForm = () => {
    setFormData({
      cityID: '',
      arrivalTime: '',
      orderID: 1
    });
    setEditingCity(null);
    setShowAddForm(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get next available order ID
  const getNextOrderID = async () => {
    try {
      const response = await routeCityApi.getNextOrderId(routeId);
      return response || 1;
    } catch (error) {
      console.error('Error getting next order ID:', error);
      // Fallback to local calculation
      if (cities.length === 0) return 1;
      const maxOrder = Math.max(...cities.map(city => city.orderID));
      return maxOrder + 1;
    }
  };

  // Show add form
  const handleAddCity = async () => {
    const nextOrderId = await getNextOrderID();
    setFormData({
      cityID: '',
      arrivalTime: '',
      orderID: nextOrderId
    });
    setEditingCity(null);
    setShowAddForm(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cityID || !formData.arrivalTime) {
      Swal.fire('Error', 'Please fill in all required fields', 'error');
      return;
    }

    // Check if order ID is already taken
    if (isOrderIdTaken(formData.orderID, editingCity?.routeCityID)) {
      Swal.fire('Error', `Order ID ${formData.orderID} is already taken by another city in this route. Please choose a different order ID.`, 'error');
      return;
    }

    // Check if city is already added to this route
    if (isCityAlreadyAdded(formData.cityID, editingCity?.routeCityID)) {
      const selectedCity = availableCities.find(city => city.cityID === parseInt(formData.cityID));
      Swal.fire('Error', `City "${selectedCity?.cityName}" is already added to this route. Please choose a different city.`, 'error');
      return;
    }

    try {
      if (editingCity) {
        // Update existing city
        const updatedCity = await routeCityApi.updateRouteCity(editingCity.routeCityID, {
          cityID: parseInt(formData.cityID),
          arrivalTime: formatTimeForAPI(formData.arrivalTime),
          orderID: parseInt(formData.orderID),
          updatedBy: 'User'
        });
        
        if (updatedCity) {
          Swal.fire('Success', 'City updated successfully', 'success');
        } else {
          throw new Error('Update failed - no response received');
        }
      } else {
        // Create new city
        await routeCityApi.createRouteCity({
          routeID: routeId,
          cityID: parseInt(formData.cityID),
          arrivalTime: formatTimeForAPI(formData.arrivalTime),
          orderID: parseInt(formData.orderID),
          createdBy: 'User'
        });
        Swal.fire('Success', 'City added successfully', 'success');
      }
      
      resetForm();
      loadCities();
    } catch (error) {
      console.error('Error saving city:', error);
      const errorMessage = error.message || 'Failed to save city';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  // Handle edit city
  const handleEditCity = (city) => {
    setFormData({
      cityID: city.cityID,
      arrivalTime: city.arrivalTime,
      orderID: city.orderID
    });
    setEditingCity(city);
    setShowAddForm(true);
  };

  // Handle delete city
  const handleDeleteCity = async (city) => {
    const result = await Swal.fire({
      title: 'Delete City',
      text: `Are you sure you want to permanently delete "${city.cityName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete permanently!'
    });

    if (result.isConfirmed) {
      try {
        await routeCityApi.deleteRouteCity(city.routeCityID);
        Swal.fire('Deleted!', 'City has been permanently removed from the database.', 'success');
        loadCities();
      } catch (error) {
        console.error('Error deleting city:', error);
        Swal.fire('Error', error.message || 'Failed to delete city', 'error');
      }
    }
  };

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString || timeString === '00:00') return 'Not Set';
    // Handle both HH:mm and HH:mm:ss formats
    try {
      const time = new Date(`2000-01-01T${timeString}`);
      return time.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      return timeString || 'Invalid Time';
    }
  };

  // Convert time input to proper format for API
  const formatTimeForAPI = (timeString) => {
    if (!timeString) return '';
    // Ensure time is in HH:mm format
    return timeString.substring(0, 5);
  };

  // Check if order ID is already taken by another city in the same route
  const isOrderIdTaken = (orderId, excludeCityId = null) => {
    return cities.some(city => 
      city.orderID === orderId && 
      city.routeCityID !== excludeCityId
    );
  };

  // Check if city is already added to this route
  const isCityAlreadyAdded = (cityID, excludeCityId = null) => {
    return cities.some(city => 
      city.cityID === parseInt(cityID) && 
      city.routeCityID !== excludeCityId
    );
  };

  // Get available cities (excluding already added ones)
  const getAvailableCities = () => {
    if (!editingCity) {
      // For new cities, exclude already added ones
      const addedCityIds = cities.map(city => city.cityID);
      return availableCities.filter(city => !addedCityIds.includes(city.cityID));
    } else {
      // For editing, include the current city
      return availableCities;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden mx-2">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold truncate text-white">Manage Route Cities</h2>
              <p className="text-blue-100 mt-1 text-sm sm:text-base truncate">{routeName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-100 transition-colors ml-2 flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {/* Add City Button */}
          <div className="mb-4 sm:mb-6">
            <button
              onClick={handleAddCity}
              className="bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center sm:justify-start space-x-2 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm sm:text-base">Add City</span>
            </button>
          </div>

                    {/* Add/Edit Form */}
          {showAddForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 sm:mb-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                {editingCity ? 'Edit City' : 'Add New City'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    {loadingCities ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg flex items-center space-x-2">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        <span className="text-gray-500">Loading cities...</span>
                      </div>
                    ) : (
                      <select
                        name="cityID"
                        value={formData.cityID}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select a city</option>
                        {getAvailableCities().map((city) => (
                          <option key={city.cityID} value={city.cityID}>
                            {city.cityName}
                          </option>
                        ))}
                      </select>
                    )}
                    {getAvailableCities().length === 0 && !loadingCities && (
                      <p className="mt-1 text-sm text-orange-600">
                        {editingCity ? 'No cities available' : 'All cities have been added to this route'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Arrival Time *
                    </label>
                    <input
                      type="time"
                      name="arrivalTime"
                      value={formData.arrivalTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order *
                    </label>
                    <input
                      type="number"
                      name="orderID"
                      value={formData.orderID}
                      onChange={handleInputChange}
                      min="1"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isOrderIdTaken(formData.orderID, editingCity?.routeCityID) 
                          ? 'border-red-500 bg-red-50' 
                          : 'border-gray-300'
                      }`}
                      required
                    />
                    {isOrderIdTaken(formData.orderID, editingCity?.routeCityID) && (
                      <p className="mt-1 text-sm text-red-600">
                        Order ID {formData.orderID} is already taken by another city in this route
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    {editingCity ? 'Update City' : 'Add City'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

                    {/* Cities List */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-3 sm:px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Route Cities</h3>
              <p className="text-sm text-gray-600">
                {loading ? 'Loading...' : `${cities.length} cities found`}
                {cities.length > 6 && (
                  <span className="ml-2 text-blue-600">• Scroll to see more</span>
                )}
              </p>
            </div>
            
            {loading ? (
              <div className="p-6 sm:p-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600 text-sm sm:text-base">Loading cities...</p>
              </div>
            ) : cities.length === 0 ? (
              <div className="p-6 sm:p-8 text-center">
                <MapPin className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No cities added yet</h3>
                <p className="text-gray-500 text-sm sm:text-base">Add cities to define the route stops</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-80 overflow-y-auto relative">
                <table className="w-full min-w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-sm">Order</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-sm">City</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-sm">Arrival Time</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cities.map((city) => (
                      <tr key={city.routeCityID} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <Hash className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                            </div>
                            <span className="font-semibold text-gray-900 text-sm sm:text-base">{city.orderID}</span>
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-900 text-sm sm:text-base truncate">
                              {city.cityName || 'Unknown City'}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex items-center space-x-2">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700 text-sm sm:text-base">{formatTime(city.arrivalTime)}</span>
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex space-x-1 sm:space-x-2">
                            <button
                              onClick={() => handleEditCity(city)}
                              className="text-blue-600 hover:text-blue-700 p-1.5 sm:p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit City"
                            >
                              <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCity(city)}
                              className="text-red-600 hover:text-red-700 p-1.5 sm:p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete City"
                            >
                              <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Scroll indicator */}
                {cities.length > 6 && (
                  <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteCityModal;
